import React, { useEffect, useMemo, useRef, useState } from "react";
import FrameAnimator from "@shared/components/Notification/components/FrameAnimator";
import Lottie from "lottie-react";
import loadingAnimation from "@assets/loading_anim.json";
import { taskService } from "@services/TaskService";
import ReportTracker from "./components/ReportTracker";
import { clearAllProgressEntries } from "@pages/Report/utils/reportProgressUtils";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@pages/Project/utils/utils";
import { useAppContext } from "@shared/context/AppContext";

export interface ReportTask {
  task_id: string;
  type: string;
  state: string;
  result: string;
  worker: string;
  project_public_id: string;
  template_name: string;
  current_step: string;
  started_at: string;
  created_at: string;
}

const FileNotificationBar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [reportTasks, setReportTasks] = useState<ReportTask[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);
  const { refreshTasksTrigger } = useAppContext();

  const navigate = useNavigate();

  useEffect(() => {
    fetchTasks();
  }, [refreshTasksTrigger]);

  const handleTaskDelete = async (taskId: string) => {
    try {
      await taskService.deleteTask(taskId);
      setReportTasks((prevTasks) =>
        prevTasks.filter((task) => task.task_id !== taskId)
      );
    } catch (error) {
      console.error("Failed to delete task:", error);
    } finally {
      fetchTasks();
    }
  };

  const fetchTasks = async () => {
    const allTasks = await taskService.getAllTasks();

    const filtered = allTasks.tasks.filter(
      (t: ReportTask) =>
        t.type === "REPORT_GENERATION" &&
        t.task_id !== "null" &&
        t.task_id !== null &&
        t.task_id !== ""
    );

    const sorted = filtered.sort((a: any, b: any) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateA - dateB;
    });

    setReportTasks(sorted);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleTaskDone = (task_id: string) => {
    fetchTasks();
  };

  const allCompleted = useMemo(() => {
    return (
      reportTasks.length > 0 &&
      reportTasks.every(
        (task) => task.state === "SUCCESS" || task.state === "FAILURE"
      )
    );
  }, [reportTasks]);

  useEffect(() => {
    if (allCompleted) {
      clearAllProgressEntries();
    }
  }, [allCompleted]);

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        className="relative"
        type="button"
      >
        <FrameAnimator
          playing={reportTasks.length > 0}
          completed={allCompleted}
          no_task={reportTasks.length === 0}
        />
        {allCompleted && (
          <span className="absolute top-[-3px] right-[-10px] rounded-full">
            <img src="/frames/done.svg" alt="done" className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      <div
        ref={popupRef}
        className="absolute right-0 mt-[25px] w-[26rem] bg-white border rounded-lg shadow-lg z-50 max-h-[22rem] overflow-y-scroll scrollbar-hide"
        style={{ display: open ? "block" : "none" }}
      >
        {reportTasks.length > 0 ? (
          reportTasks.map((report, index) => (
            <div
              key={report.task_id}
              className={`flex flex-col py-2 px-2 w-full min-w-96 h-[6.5rem] ${
                index !== 0 ? "border-t" : ""
              }`}
            >
              <div className="flex items-start gap-3 w-full h-[6.5rem] relative">
                <div className="flex-shrink-0 flex items-center my-auto max-w-[20%]">
                  {report.state == "SUCCESS" ? (
                    <img
                      src="/report_generation_complete.svg"
                      alt="Done"
                      className="w-[65px] h-[65px] p-[0.575rem]"
                    />
                  ) : (
                    <Lottie
                      animationData={loadingAnimation}
                      style={{ width: 65 }}
                    />
                  )}
                </div>

                <div className="w-full max-w-[80%]">
                  <div className="flex flex-col flex-grow">
                    <div className="text-xs 2xl:text-sm font-medium text-gray-800 truncate pr-6">
                      {report.template_name} -{" "}
                      {formatDate(new Date(report.created_at))}
                    </div>

                    <ReportTracker
                      taskId={report.task_id}
                      onDone={() => handleTaskDone(report.task_id)}
                      completed={report.state === "SUCCESS"}
                      onDelete={handleTaskDelete}
                      failed={
                        report.state === "FAILURE" || report.state === "REVOKED"
                      }
                      currentStep={report.current_step}
                    />
                  </div>
                  {report.state === "SUCCESS" && (
                    <button className="text-right self-end float-right">
                      <button
                        onClick={() =>
                          navigate(
                            `/report/${parseInt(
                              JSON.parse(report.result),
                              10
                            )}/project/${report.project_public_id}`
                          )
                        }
                        className="text-blue-600 text-xs 2xl:text-sm font-medium hover:underline"
                      >
                        View Report
                      </button>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center p-4 text-gray-500">
            <p className="text-xs 2xl:text-sm">
              No ongoing report generation tasks.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileNotificationBar;
